
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";
import { ChatMessage } from "./types";

/**
 * Sends a message to the Live Chat Assistant and returns the response.
 */
export async function sendChatMessage(message: string, conversationHistory: ChatMessage[] = []): Promise<ChatMessage> {
  try {
    // Filter out only the messages needed for context (exclude system messages)
    const historyForAPI = conversationHistory
      .filter(msg => msg.role !== 'system')
      .map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp
      }));
    
    console.log(`Sending message with ${historyForAPI.length} history items`);
    
    const { data, error } = await supabase.functions.invoke("live-chat-assistant", {
      body: { 
        message,
        conversation_history: historyForAPI
      },
    });

    if (error) {
      console.error("Error from live-chat-assistant function:", error);
      throw new Error(`Failed to get response: ${error.message}`);
    }

    // Create a properly formatted response message
    const responseMessage: ChatMessage = {
      id: uuidv4(),
      content: data.response || "I'm sorry, I couldn't process your request.",
      role: "assistant",
      timestamp: Date.now(),
      scriptureReferences: data.scriptureReferences || [],
      recommendedTools: data.recommendedTools || []
    };

    return responseMessage;
  } catch (error) {
    console.error("Error in sendChatMessage:", error);
    
    // Return a fallback message if something goes wrong
    return {
      id: uuidv4(),
      content: "I apologize, but I'm having trouble connecting to the server. Please try again later.",
      role: "assistant",
      timestamp: Date.now()
    };
  }
}

/**
 * Saves chat messages to local storage.
 */
export function saveChatToLocalStorage(messages: ChatMessage[]): void {
  try {
    localStorage.setItem("selah_chat_history", JSON.stringify(messages));
  } catch (error) {
    console.error("Error saving chat history to localStorage:", error);
  }
}

/**
 * Loads chat messages from local storage.
 */
export function loadChatFromLocalStorage(): ChatMessage[] {
  try {
    const savedChat = localStorage.getItem("selah_chat_history");
    return savedChat ? JSON.parse(savedChat) : [];
  } catch (error) {
    console.error("Error loading chat history from localStorage:", error);
    return [];
  }
}

/**
 * Clears chat history from local storage.
 */
export function clearChatHistory(): void {
  try {
    localStorage.removeItem("selah_chat_history");
  } catch (error) {
    console.error("Error clearing chat history from localStorage:", error);
  }
}
